import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiSearch, HiChevronLeft, HiChevronRight, HiDownload } from 'react-icons/hi';
import { fetchEmailLeadsList, fetchAllEmailsList } from '../../services/emailLeadsService';

const itemsPerPage = 10;

const emailLeadsCache = {
  rows: null,
  page: 1,
  totalRecords: 0,
  firstLoad: true,
  lastSearch: '',
  error: null,
  inFlightKey: null,
  inFlightPromise: null,
};

const normalizeLead = (item, index) => ({
  ...item,
  id: item._id || item.id || `lead-${index}`,
  email: item.email || item.userEmail || item.user || '—',
  createdAt: item.createdAt || item.created_at || item.created_at,
  country: item.country || null,
  state: item.state || null,
});

const extractItems = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.docs)) return res.data.docs;
  if (Array.isArray(res?.data?.list)) return res.data.list;
  if (Array.isArray(res?.data?.leads)) return res.data.leads;
  if (Array.isArray(res?.result)) return res.result;
  if (Array.isArray(res?.list)) return res.list;
  if (Array.isArray(res?.leads)) return res.leads;
  return [];
};

const extractTotal = (response, itemsLength, currentPage, pageSize) => {
  const total =
    response?.total ??
    response?.data?.total ??
    response?.data?.totalDocs ??
    response?.data?.count ??
    response?.data?.pagination?.total ??
    response?.data?.pagination?.totalRecords ??
    response?.data?.pagination?.totalCount ??
    response?.pagination?.total ??
    response?.pagination?.totalRecords ??
    null;

  if (total !== null && total !== undefined) return total;
  if (itemsLength === pageSize) return currentPage * pageSize + 1;
  if (itemsLength > 0) return (currentPage - 1) * pageSize + itemsLength;
  return 0;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const hasReadyCache = () =>
  Array.isArray(emailLeadsCache.rows) &&
  emailLeadsCache.rows !== null &&
  !emailLeadsCache.firstLoad;

const EmailLeads = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(emailLeadsCache.lastSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(emailLeadsCache.lastSearch || '');
  const [currentPage, setCurrentPage] = useState(emailLeadsCache.page || 1);
  const [rows, setRows] = useState(emailLeadsCache.rows || []);
  const [totalRecords, setTotalRecords] = useState(emailLeadsCache.totalRecords || 0);
  // Show loader until first successful load (e.g. full refresh) or while re-fetching; avoid empty-state flash.
  const [isLoading, setIsLoading] = useState(() => !hasReadyCache());
  const [error, setError] = useState(emailLeadsCache.error || null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadStartDate, setDownloadStartDate] = useState('');
  const [downloadEndDate, setDownloadEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [startDateEnabled, setStartDateEnabled] = useState(false);
  const [endDateEnabled, setEndDateEnabled] = useState(false);
  const searchDebounceRef = useRef(null);
  const isInitialDebounceMountRef = useRef(true);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    // On initial mount we already seeded `debouncedSearch` from cache,
    // so we avoid scheduling an extra timer + API call.
    if (isInitialDebounceMountRef.current) {
      isInitialDebounceMountRef.current = false;
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage((p) => (p !== 1 ? 1 : p));
    }, 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;

    const hasCache = hasReadyCache();

    const isSameParams =
      emailLeadsCache.page === currentPage &&
      (emailLeadsCache.lastSearch || '') === debouncedSearch;

    if (hasCache && isSameParams) {
      setRows(emailLeadsCache.rows || []);
      setTotalRecords(emailLeadsCache.totalRecords || 0);
      setError(emailLeadsCache.error || null);
      setIsLoading(false);
      return;
    }

    const cacheKey = `${currentPage}|${itemsPerPage}|${debouncedSearch || ''}`;

    // If a request for the same params is already running (e.g., StrictMode double-mount),
    // don't start another API call. Just wait for the in-flight promise to populate cache.
    const shouldWaitForInFlight =
      emailLeadsCache.inFlightPromise &&
      emailLeadsCache.inFlightKey === cacheKey;

    const applyCacheToUI = () => {
      setRows(emailLeadsCache.rows || []);
      setTotalRecords(emailLeadsCache.totalRecords || 0);
      setError(emailLeadsCache.error || null);
    };

    if (shouldWaitForInFlight) {
      // Keep loader visible (initial load / Strict Mode remount) until the in-flight fetch finishes.
      setIsLoading(true);
      emailLeadsCache.inFlightPromise
        .then(() => {
          if (cancelled) return;
          applyCacheToUI();
          setIsLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          applyCacheToUI();
          setIsLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    const fetchAndPopulateCache = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchEmailLeadsList(
          currentPage,
          itemsPerPage,
          debouncedSearch
        );
        const items = extractItems(response);
        const normalized = items.map((item, idx) =>
          normalizeLead(item, idx)
        );

        const computedTotal = extractTotal(
          response,
          items.length,
          currentPage,
          itemsPerPage
        );

        emailLeadsCache.rows = normalized;
        emailLeadsCache.page = currentPage;
        emailLeadsCache.totalRecords = computedTotal;
        emailLeadsCache.lastSearch = debouncedSearch;
        emailLeadsCache.error = null;
        emailLeadsCache.firstLoad = false;
      } catch (err) {
        const errorMsg = err.message || 'Failed to load email list.';
        emailLeadsCache.rows = [];
        emailLeadsCache.totalRecords = 0;
        emailLeadsCache.error = errorMsg;
        emailLeadsCache.page = currentPage;
        emailLeadsCache.lastSearch = debouncedSearch;
        emailLeadsCache.firstLoad = false;
      } finally {
        // Clear in-flight markers (even if it failed).
        if (emailLeadsCache.inFlightKey === cacheKey) {
          emailLeadsCache.inFlightKey = null;
          emailLeadsCache.inFlightPromise = null;
        }
      }
    };

    emailLeadsCache.inFlightKey = cacheKey;
    emailLeadsCache.inFlightPromise = fetchAndPopulateCache();

    emailLeadsCache.inFlightPromise
      .then(() => {
        if (cancelled) return;
        applyCacheToUI();
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        applyCacheToUI();
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentPage, debouncedSearch]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  const getPaginationRange = () => {
    if (!totalPages || totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    const pages = [firstPageIndex];
    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) pages.push('dots');
    for (let page = startPage; page <= endPage; page += 1) pages.push(page);
    if (shouldShowRightDots) pages.push('dots');
    pages.push(lastPageIndex);

    return pages;
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDownloadClick = () => {
    const today = new Date().toISOString().split('T')[0];
    const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0];
    setDownloadStartDate(firstDayOfMonth);
    setDownloadEndDate(today);
    setStartDateEnabled(true);
    setEndDateEnabled(true);
    setShowDownloadModal(true);
  };

  const handleDownloadConfirm = async () => {
    const useDateFilter = startDateEnabled && endDateEnabled;
    if (useDateFilter && (!downloadStartDate || !downloadEndDate)) return;
    setIsDownloading(true);
    try {
      const response = await fetchAllEmailsList(
        useDateFilter ? downloadStartDate : null,
        useDateFilter ? downloadEndDate : null
      );
      const leads = response?.data?.leads ?? [];
      const blob = new Blob([JSON.stringify({ leads }, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-leads-${downloadStartDate}-to-${downloadEndDate}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowDownloadModal(false);
    } catch (err) {
      alert(err.message || 'Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                  Email List
                </h1>
              <button
                onClick={handleDownloadClick}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-opacity"
              >
                <HiDownload className="w-4 h-4" />
                Download All
              </button>
            </div>
            {/* <p className="text-sm text-gray-600 mt-1 md:ml-0">
              Search and browse captured email addresses from the admin API.
            </p> */}
          </div>
          <div className="mb-4 md:mb-6">
          <div className="w-full md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] focus:outline-none   bg-white"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>
              </div>
          {isLoading ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500 pointer-events-auto text-sm">
                No email list match your search.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[700px] table-fixed border-collapse">
                  <thead className="bg-white border-b  border-[#ECECEC]">
                    <tr>
                      <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        State
                      </th>
                      <th className="px-3 md:px-4 py-3 text-right w-[180px] pr-16 md:pr-56 pl-10 ml-10 md:ml-9 md:pl-10 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#ECECEC]">
                    {rows.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/80">
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-900 break-all">
                          {item.email}
                        </td>
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-700">
                          {item.country || '—'}
                        </td>
                        <td className="px-3 md:px-4 py-2.5 text-sm text-gray-700">
                          {item.state || '—'}
                        </td>
                        <td className="py-3 text-sm text-gray-700 whitespace-nowrap text-right pr-10 md:pr-56 pl-2">
                          {formatDate(item.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 0 && (
                <div className="px-4 md:px-6 py-4 flex justify-end border-t border-[#ECECEC]">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Previous page"
                    >
                      <HiChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1">
                      {getPaginationRange().map((page, idx) =>
                        page === 'dots' ? (
                          <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                              page === currentPage
                                ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                                : 'text-gray-900 hover:bg-gray-100'
                            }`}
                            aria-current={page === currentPage ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      <HiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {showDownloadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Download Email Leads
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Toggle date filter to download emails within a specific range.
              </p>

              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Filter by Date</span>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDateEnabled(!startDateEnabled);
                      setEndDateEnabled(!endDateEnabled);
                      if (startDateEnabled && endDateEnabled) {
                        setDownloadStartDate('');
                        setDownloadEndDate('');
                      }
                    }}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                      startDateEnabled && endDateEnabled ? 'bg-brand' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        startDateEnabled && endDateEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={downloadStartDate}
                    onChange={(e) => setDownloadStartDate(e.target.value)}
                    disabled={!(startDateEnabled && endDateEnabled)}
                    className="w-full px-3 py-2 text-sm border border-[#ECECEC] rounded-lg focus:outline-none focus:border-brand disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={downloadEndDate}
                    onChange={(e) => setDownloadEndDate(e.target.value)}
                    disabled={!(startDateEnabled && endDateEnabled)}
                    className="w-full px-3 py-2 text-sm border border-[#ECECEC] rounded-lg focus:outline-none focus:border-brand disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-5">
                <button
                  onClick={() => setShowDownloadModal(false)}
                  disabled={isDownloading}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDownloadConfirm}
                  disabled={
                    (startDateEnabled && endDateEnabled) &&
                    (!downloadStartDate || !downloadEndDate) ||
                    isDownloading
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDownloading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Downloading...
                    </>
                  ) : (
                    'Download'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailLeads;
